import { useCallback, useMemo } from 'react';
import type { FleetCompleteOrderErrorCodesType } from 'pos-client/components/orders/Fleet/constants/constants.ts';
import {
  isJobStatus,
  isOrderStatus,
} from 'pos-client/utils/order/jobBuilderUtils.ts';
import { standardCachingQueryOptions } from 'pos-client/utils/standardCachingQueryOptions.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { NoteModel } from 'pos-shared/models/noteSchema.ts';
import type {
  OrderCustomerDetailsFeModel,
  OrderFeModel,
  OrderJobFeModel,
  OrderJobItemFeModel,
  OrderJobItemPartFeModel,
  OrderJobPromotionDetailsFeModel,
  OrderJobTechnicianFeModel,
  OrderScheduleDetailsModel,
} from 'pos-shared/models/orderSchema.ts';
import type { OrderTotalModel } from 'pos-shared/models/pricingSchema.ts';
import type { OrderStatusType } from 'pos-shared/models/statusSchema.ts';

import { useLocation } from './useLocations.tsx';
import { useOrderPayments } from './useOrderPayments.ts';
import { useVehicle } from './useVehicle.tsx';

interface UseOrderProps {
  orderId?: string;
  vehicleId?: string;
  locationId?: string;
  bulkActionJobs?: OrderJobFeModel[];
}

export const useOrder = ({ orderId, vehicleId, locationId }: UseOrderProps) => {
  const {
    data,
    isLoading: isLoadingOrder,
    isFetching: isFetchingOrder,
    refetch: refetchOrder,
    ...rest
  } = trpc.orders.getOrder.useQuery(
    {
      id: orderId as string,
      includeCustomer: true,
      includeJobs: true,
      includeJobItems: true,
      includeOrderSchedules: true,
      includeOrderNotes: true,
      includeJobFulfilmentTechnicians: true,
      includeCreditReleaseStatus: true,
      includeOrderAlerts: true,
      includeTireLabels: true,
      includeJobAnswers: false, // integration coming soon
      // NOTE: we load all of these each time, as it's a single trip and then it's in the cache for every other page & tab
    },
    {
      enabled: !!orderId,
      ...standardCachingQueryOptions,
      select: (data) => {
        const { jobs, orderSchedules, customer, ...rest } = data;
        return {
          ...rest,
          orderSchedules: (orderSchedules ??
            []) as unknown as OrderScheduleDetailsModel[], // hack because optional
          jobs: (jobs ?? []) as unknown as OrderJobFeModel[], // hack because optional
          customer: customer as unknown as OrderCustomerDetailsFeModel, // hack because optional
        };
      },
    }
  );

  // Ability to modify discount by order status
  const { orderPayments } = useOrderPayments({ orderId: orderId as string });
  const orderStatus = data?.status as OrderStatusType;
  const outstandingAmount =
    orderPayments?.paymentsTotal.totalOutstanding.incTax ?? 0;
  const disableModifyDiscountByOrderStatus =
    isOrderStatus(orderStatus, ['Completed', 'Cancelled']) ||
    (isOrderStatus(orderStatus, 'Invoiced') && outstandingAmount === 0);

  // extract all jobs across order
  const orderJobs = useMemo(() => data?.jobs || [], [data?.jobs]);

  const orderTotal = data?.total as OrderTotalModel;

  const jobNotes =
    data?.jobs?.flatMap(
      (job) =>
        job.notes?.map((note) => ({
          ...note,
          noteSource: 'job',
          noteType: 'Job',
          jobId: job.jobId,
          jobRef: job.reference,
        })) as NoteModel[]
    ) ?? [];

  // extract all tech app notes across order, format like other notes
  const techAppNotes = orderJobs
    ?.flatMap((job) =>
      job.techAppNotes?.noteContents?.flatMap((techAppNote) =>
        techAppNote?.meta.note.map((noteItem) => ({
          noteSource: 'techApp',
          noteId: techAppNote?.id,
          noteType: 'Job',
          jobId: job.jobId,
          facingType: noteItem?.facingType || 'Order',
          noteContent: noteItem?.text,
          addedBy: job.technicians
            .map((technician) => technician.name)
            .join(', '),
          dateAdded: noteItem ? new Date(noteItem.createdAt) : '',
          jobRef: job.reference,
          images: noteItem?.images.map((image) => image.uri),
        }))
      )
    )
    .filter(Boolean) as NoteModel[];

  // extract all job items across order
  const orderJobItems = orderJobs.flatMap(
    (j) => j.items
  ) as OrderJobItemFeModel[];

  // extract all parts across order
  const orderJobParts = orderJobItems.flatMap(
    (i) => i.parts
  ) as OrderJobItemPartFeModel[];

  // extract all promotions across order
  const orderJobPromotionDetails = orderJobs.flatMap(
    (j) => j.promotionDetails
  ) as OrderJobPromotionDetailsFeModel[];

  // extract all technicians across order
  const orderTechnicians = (orderJobs || []).flatMap(
    ({ technicians }) => technicians
  ) as OrderJobTechnicianFeModel[];

  const allJobsCompletedOrCancelled = orderJobs.every((job) =>
    isJobStatus(job.status, ['Complete', 'Cancelled'])
  );

  const partNotes = orderJobParts.flatMap(
    (part) =>
      part.notes?.map((note) => ({ ...note, item: part.description })) ?? []
  );

  const notesConcat = jobNotes
    .concat(data?.notes ?? [])
    .concat(techAppNotes)
    .concat(partNotes);

  // sort notes by date descending
  const allNotes = notesConcat.sort((a, b) => {
    return (
      (b.dateAdded ? new Date(b.dateAdded).getTime() : 0) -
      (a.dateAdded ? new Date(a.dateAdded).getTime() : 0)
    );
  });

  // Wont trigger unless locationId is provided
  const { location, isLoadingLocation } = useLocation(locationId);
  // Wont trigger unless vehicleId is provided
  const { vehicle, isLoadingVehicle } = useVehicle({ vehicleId });

  const isLoadingAll =
    isLoadingOrder ||
    (vehicleId ? isLoadingVehicle : false) ||
    (locationId ? isLoadingLocation : false);

  const orderPaymentJobs = useMemo(
    () =>
      orderJobs.filter(
        ({ status }) => !isJobStatus(status, ['Quoted Job', 'Cancelled'])
      ),
    [orderJobs]
  );

  const getDrCreateErrorsByCodes = useCallback(
    (codes: FleetCompleteOrderErrorCodesType[]) => {
      return (
        data?.b2BOrderStatus?.b2BTreadNetStatus?.headerErrors?.filter((error) =>
          codes.includes(error.errorId)
        ) ?? []
      );
    },
    [data?.b2BOrderStatus?.b2BTreadNetStatus]
  );

  return {
    order: data as OrderFeModel | undefined,
    orderCustomer: data?.customer as OrderCustomerDetailsFeModel | undefined,
    allNotes,
    orderNotes: data?.notes as NoteModel[] | undefined,
    jobNotes,
    techAppNotes,
    orderJobs,
    orderJobItems,
    orderJobParts,
    orderJobPromotionDetails,
    orderTechnicians,
    orderTotal,
    getDrCreateErrorsByCodes,
    allJobsCompletedOrCancelled,
    orderPaymentJobs,
    isLoadingOrder,
    isFetchingOrder,
    refetchOrder,
    isLoadingLocation,
    location,
    isLoadingVehicle,
    vehicle,
    isLoadingAll,
    disableModifyDiscountByOrderStatus,
    ...rest,
  };
};
